import { ActivityEntityName, EntityViewDisplay, JobTemplateEntity, JobTemplateSecurityRight, SurfaceType } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { jobTemplateColumnOverrides, overrideJobTemplateCellRenderer } from './board/job.template.board.cell.renderers';
import { jobTemplateRowCommands } from './commands/job.template.delete.command';
import { JobTemplateRelationName } from './relation.names';
import { JOB_TEMPLATE_ALL_VIEW_ID, JOB_TEMPLATE_SLUG, jobTemplateRecordRoute, navigateToJobTemplateManage } from './services/job.template.board.open';

/**
 * Staged under finance-pro alongside the other v2 boards. The legacy `/jobs/job-templates` screens are
 * untouched and stay the live UI.
 */
export const jobTemplateManifest = registerManifest<JobTemplateEntity>({
  entity: ActivityEntityName.JobTemplate,
  slug: JOB_TEMPLATE_SLUG,

  board: {
    supportedDisplays: [EntityViewDisplay.Table],
    defaults: {
      header: {
        title: 'Job Templates',
        showPaginator: true,
        defaultViewId: JOB_TEMPLATE_ALL_VIEW_ID,
      },
      itemInteraction: {
        navigate: navigateToJobTemplateManage,
        // Legacy listed a trash button on every row; the board runs the factory in an injection context.
        commands: template => jobTemplateRowCommands(template),
      },
      table: {
        columns: jobTemplateColumnOverrides,
        overrideCellRenderer: overrideJobTemplateCellRenderer,
      },
    },
  },

  surfaces: {
    [SurfaceType.Creator]: () => import('./surface/creator/job.template.creator.surface').then(module => module.jobTemplateCreatorSurface),
    [SurfaceType.Manage]: () => import('./surface/manage/job.template.manage.surface').then(module => module.jobTemplateManageSurface),
  },

  operations: {
    // Legacy opened the new record after Save. Without this flag the dialog host only closes, and
    // the next step is hunting the row on the board.
    create: {
      surface: SurfaceType.Creator,
      navigateOnCreate: true,
      presentation: { mode: 'dialog', width: 'min(640px, calc(100vw - 48px))', height: 'auto', maxHeight: 'calc(100vh - 48px)' },
      authorize: ({ injector }) => injector.get(RightsService).hasRights(JobTemplateSecurityRight.Manage),
    },
    edit: { surface: SurfaceType.Manage },
  },

  routes: {
    root: '/finance-pro/job-templates',
    record: jobTemplateRecordRoute,
  },

  relations: {
    [JobTemplateRelationName.Tasks]: () => import('./relations/job.template.tasks.relation').then(module => module.jobTemplateTasksBoardBuilder),
    [JobTemplateRelationName.Questions]: () => import('./relations/job.template.questions.relation').then(module => module.jobTemplateQuestionsBoardBuilder),
  },
});
